import React, { useEffect } from 'react';
import useSettingsStore from './store/settingsStore';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import trTR from 'antd/locale/tr_TR';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import useAuthStore from './store/authStore';
import api from './api/axios';
import MainLayout from './layouts/MainLayout';
import ParentLayout from './layouts/ParentLayout';
import Login from './pages/Login';
import ParentActivate from './pages/ParentActivate';
import ChangePassword from './pages/ChangePassword';
import NotificationRedirect from './pages/NotificationRedirect';
import Dashboard from './pages/Dashboard';
import Branches from './pages/Branches';
import BranchDetail from './pages/BranchDetail';
import Users from './pages/Users';
import UserDetail from './pages/UserDetail';
import Students from './pages/Students';
import StudentDetail from './pages/StudentDetail';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import Settings from './pages/Settings';
import Payments from './pages/Payments';
import ExpenseSchedule from './pages/ExpenseSchedule';
import Announcements from './pages/Announcements';
import Calendar from './pages/Calendar';
import Profile from './pages/Profile';
import ParentDashboardPage from './pages/parent/ParentDashboard';
import ParentStudents from './pages/parent/ParentStudents';
import ParentPayments from './pages/parent/ParentPayments';
import ParentAnnouncements from './pages/parent/ParentAnnouncements';
import ParentProfile from './pages/parent/ParentProfile';
import ParentCompetitions from './pages/parent/ParentCompetitions';
import CertificateVerify from './pages/CertificateVerify';
import Reports from './pages/Reports';
import Anomalies from './pages/Anomalies';
import DailyNotes from './pages/DailyNotes';
import CertificateDeliveries from './pages/CertificateDeliveries';
import InternDashboard from './pages/InternDashboard';
import InternMedia from './pages/InternMedia';
import Agenda from './pages/Agenda';
import WorkTasks from './pages/WorkTasks';
import WorkTaskDetail from './pages/WorkTaskDetail';
import ParentRequests from './pages/ParentRequests';
import Notifications from './pages/Notifications';
import ConsentTexts from './pages/ConsentTexts';
import StudentBadgeTypes from './pages/StudentBadgeTypes';
import Schools from './pages/Schools';
import LeaveRequests from './pages/LeaveRequests';
import Delegations from './pages/Delegations';
import Preschools from './pages/Preschools';
import PreschoolCertificateVerify from './pages/PreschoolCertificateVerify';
import PreschoolAgreementDetail from './pages/PreschoolAgreementDetail';
import Routines from './pages/Routines';
import Interns from './pages/Interns';
import InternMyDays from './pages/InternMyDays';
import Competitions from './pages/Competitions';
import CompetitionDetail from './pages/CompetitionDetail';
import CompetitionPayments from './pages/CompetitionPayments';
import IdleSessionGuard from './components/common/IdleSessionGuard';

dayjs.locale('tr');

const useAuth = () => useAuthStore();

// Giriş yapılmamışsa login'e yönlendir
// permission: rol yetmediğinde kişi bazında verilen izne bakılır. Bu olmadan
// Kullanıcılar > Yetkiler'den bir öğretmene competitions.view verildiğinde
// menü çıksa bile sayfa açılırken dashboard'a geri atıyordu.
// Not: burası yalnızca görünürlük; asıl yetki backend'deki permission
// middleware'inde (bkz. frontend/CLAUDE.md "UI'da gizlemek yetki değildir").
const ProtectedRoute = ({ children, roles, permission }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.first_login) return <Navigate to="/change-password" />;

  const hasPermission = permission && (user?.permissions || []).includes(permission);
  if (roles && !roles.includes(user?.role) && !hasPermission) return <Navigate to="/dashboard" />;

  return children;
};

// Veli rotaları
const ParentRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.first_login) return <Navigate to="/change-password" />;
  if (user?.role !== 'parent') return <Navigate to="/dashboard" />;
  return children;
};

// Personel rotaları (veli ve stajyer giremez)
const StaffRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.first_login) return <Navigate to="/change-password" />;
  if (user?.role === 'parent') return <Navigate to="/parent/dashboard" />;
  if (user?.role === 'stajyer') return <Navigate to="/intern/today" />;
  return children;
};

// Stajyer rotaları
const InternRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.first_login) return <Navigate to="/change-password" />;
  if (user?.role !== 'stajyer') return <Navigate to="/dashboard" />;
  return children;
};

const ADMIN_ROLES = ['developer_admin', 'super_admin'];
const MANAGER_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];
const STAFF_ROLES = ['developer_admin', 'super_admin', 'branch_manager', 'teacher'];

const App = () => {
  const { appName, fetchSettings } = useSettingsStore();
  const { isAuthenticated, updateUser, logout } = useAuthStore();

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (isAuthenticated) {
      api.get('/auth/me')
        .then(res => updateUser(res.data))
        // Sadece gercek kimlik dogrulama hatasinda oturumu kapat. Eskiden
        // .catch(() => logout()) ile HER hata kullaniciyi disari atiyordu:
        // 429 (hiz siniri), 500, ag kesintisi, sekme uykudan uyanirken dusen
        // istek... Bu, dashboard'da anlik bir aksaklikta bile oturumun
        // olmesine ve kullanicinin tekrar tekrar giris yapmasina yol aciyordu.
        // 401 durumunda interceptor once token yenilemeyi deniyor; buraya
        // dusuyorsa yenileme de basarisiz olmus demektir.
        .catch(err => {
          if (err.response?.status === 401) logout();
        });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (appName) document.title = appName + ' Yönetim Sistemi';
  }, [appName]);

  return (
    <ConfigProvider
      locale={trTR}
      theme={{
        token: {
          colorPrimary:        '#1a56db',
          colorLink:           '#1a56db',
          colorSuccess:        '#12a150',
          colorWarning:        '#f59e0b',
          colorError:          '#e02424',
          borderRadius:         6,
          fontFamily:          "'Inter', 'Segoe UI', sans-serif",
        },
        components: {
          Card:   { headerBg: '#f8faff', borderRadiusLG: 10 },
          Menu:   { itemSelectedBg: '#e8f0fe', itemSelectedColor: '#1a56db', itemHoverBg: '#f0f4ff' },
          Button: { borderRadius: 6 },
          Table:  { headerBg: '#f0f4ff' },
        },
      }}
    >
      <BrowserRouter>
        <IdleSessionGuard />
        <Routes>
          {/* Anaokulu dogrulamasi /verify/:token DEN ONCE: asagidaki rota
              "anaokulu" kelimesini token sanip yakalardi. */}
          <Route path="/verify/anaokulu/:token" element={<PreschoolCertificateVerify />} />
          <Route path="/verify/:token" element={<CertificateVerify />} />
          <Route path="/login" element={<Login />} />
          {/* Veli üyeliği aktifleştirme — GİRİŞ GEREKTİRMEZ. Afişteki QR ve
              velilere gidecek mesaj bu adrese yönlendiriyor. */}
          <Route path="/uyelik-aktiflestir" element={<ParentActivate />} />
          <Route path="/change-password" element={<ChangePassword />} />

          {/*
            Web push bildirimine tıklanınca service worker buraya getiriyor.
            Rol kısıtı YOK (yalnızca oturum şart): hem veli hem personel
            bildirim alıyor ve sayfa doğru hedefi rolden hesaplıyor
            (bkz. NotificationRedirect — yönlendirme kuralı
            notificationTarget.js'te tek yerde).
          */}
          <Route
            path="/bildirim/:id"
            element={<ProtectedRoute><NotificationRedirect /></ProtectedRoute>}
          />

          {/* Stajyer Paneli */}
          <Route
            path="/intern"
            element={
              <InternRoute>
                <MainLayout />
              </InternRoute>
            }
          >
            <Route index element={<Navigate to="/intern/today" />} />
            <Route path="today" element={<InternDashboard />} />
            <Route path="media" element={<InternMedia />} />
            <Route path="profile" element={<Profile />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="agenda" element={<Agenda />} />
            <Route path="work-tasks" element={<WorkTasks />} />
            <Route path="work-tasks/:id" element={<WorkTaskDetail />} />
            <Route path="routines" element={<Routines />} />
            {/* Stajyerin kendi gün beyanı ve bakiyesi */}
            <Route path="my-days" element={<InternMyDays />} />
          </Route>

          {/* Veli Paneli */}
          <Route
            path="/parent"
            element={
              <ParentRoute>
                <ParentLayout />
              </ParentRoute>
            }
          >
            <Route index element={<Navigate to="/parent/dashboard" />} />
            <Route path="dashboard" element={<ParentDashboardPage />} />
            <Route path="students" element={<ParentStudents />} />
            <Route path="payments" element={<ParentPayments />} />
            <Route path="announcements" element={<ParentAnnouncements />} />
            {/* Bildirime tıklayınca ?participant=<id> ile buraya gelinir ve
                ilgili yarışma doğrudan açılır — onay orada verilir. */}
            <Route path="competitions" element={<ParentCompetitions />} />
            <Route path="profile" element={<ParentProfile />} />
          </Route>

          {/* Personel Paneli */}
          <Route
            path="/"
            element={
              <StaffRoute>
                <MainLayout />
              </StaffRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />
            <Route path="agenda" element={<Agenda />} />
            <Route path="work-tasks" element={<WorkTasks />} />
            <Route path="work-tasks/:id" element={<WorkTaskDetail />} />
            <Route path="routines" element={<Routines />} />
            {/* Stajyer takibi — öğretmen gün onaylar, ücret/bakiye sekmesi
                yalnızca şube müdürü ve üstüne açık (asıl kısıt backend'deki
                interns.payments.* izinlerinde). */}
            <Route path="interns" element={
              <ProtectedRoute roles={STAFF_ROLES}>
                <Interns />
              </ProtectedRoute>
            } />
            <Route path="parent-requests" element={
              <ProtectedRoute roles={STAFF_ROLES}>
                <ParentRequests />
              </ProtectedRoute>
            } />

            {/* Yarışma modülü — UI'daki rol kısıtı yalnızca görünürlük;
                asıl yetki backend'deki competitions.* izinlerinde. */}
            <Route path="competitions" element={
              <ProtectedRoute roles={ADMIN_ROLES} permission="competitions.view">
                <Competitions />
              </ProtectedRoute>
            } />
            <Route path="competitions/:id" element={
              <ProtectedRoute roles={ADMIN_ROLES} permission="competitions.view">
                <CompetitionDetail />
              </ProtectedRoute>
            } />
            {/* Bankoda yarışma tahsilatı — DAR izin, menüdeki kalemle
                AYNI anahtar. İkisinden biri eksik olsaydı yetki verilen
                öğretmen ya linki göremez ya da sayfayı açamazdı. */}
            <Route path="competition-payments" element={
              <ProtectedRoute roles={ADMIN_ROLES} permission="competitions.payments">
                <CompetitionPayments />
              </ProtectedRoute>
            } />

            <Route path="branches" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="branches.view">
                <Branches />
              </ProtectedRoute>
            } />
            <Route path="branches/:id" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="branches.view">
                <BranchDetail />
              </ProtectedRoute>
            } />

            <Route path="students" element={
              <ProtectedRoute roles={STAFF_ROLES}>
                <Students />
              </ProtectedRoute>
            } />
            <Route path="students/:id" element={
              <ProtectedRoute roles={STAFF_ROLES}>
                <StudentDetail />
              </ProtectedRoute>
            } />

            <Route path="courses" element={
              <ProtectedRoute roles={MANAGER_ROLES}>
                <Courses />
              </ProtectedRoute>
            } />
            <Route path="courses/:id" element={
              <ProtectedRoute roles={MANAGER_ROLES}>
                <CourseDetail />
              </ProtectedRoute>
            } />

            <Route path="calendar" element={
              <ProtectedRoute roles={STAFF_ROLES}>
                <Calendar />
              </ProtectedRoute>
            } />

            <Route path="payments" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="payments.view.all">
                <Payments />
              </ProtectedRoute>
            } />
            {/* ADMIN_ROLES + izin: izinler varsayilanda yalnizca super
                admin+ (Yarisma modulundeki ayni gerekce). MANAGER_ROLES
                yazilsaydi sube muduru menuyu gorur, sayfa acilir ama her
                API cagrisi 403 donerdi - izin verilmeden erisim olmamali.
                Izin Kullanicilar > Yetkiler'den kisi bazinda verilince
                `permission` kacis kapisi devreye giriyor. */}
            <Route path="preschools" element={
              <ProtectedRoute roles={ADMIN_ROLES} permission="preschools.view">
                <Preschools />
              </ProtectedRoute>
            } />
            <Route path="preschools/agreements/:id" element={
              <ProtectedRoute roles={ADMIN_ROLES} permission="preschools.view">
                <PreschoolAgreementDetail />
              </ProtectedRoute>
            } />

            {/* Ödeme çizelgesi PERSONEL MAAŞLARINI gösteriyor. Menüdeki
                kalemle AYNI izin anahtarı kullanılmalı — biri unutulursa
                yönetici yetkiyi verdiğini sanır ama kişi sayfayı açamaz
                (ya da tersi). */}
            <Route path="expense-schedule" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="expense_schedules.view">
                <ExpenseSchedule />
              </ProtectedRoute>
            } />

            <Route path="reports" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="reports.view">
                <Reports />
              </ProtectedRoute>
            } />

            <Route path="anomalies" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="anomalies.view">
                <Anomalies />
              </ProtectedRoute>
            } />

            <Route path="daily-notes" element={
              <ProtectedRoute roles={STAFF_ROLES}>
                <DailyNotes />
              </ProtectedRoute>
            } />

            <Route path="certificate-deliveries" element={
              <ProtectedRoute roles={STAFF_ROLES}>
                <CertificateDeliveries />
              </ProtectedRoute>
            } />

            <Route path="announcements" element={<Announcements />} />

            <Route path="notifications" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="announcements.create">
                <Notifications />
              </ProtectedRoute>
            } />
            <Route path="consent-texts" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="kvkk.view">
                <ConsentTexts />
              </ProtectedRoute>
            } />
            <Route path="student-badge-types" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="student_badge_types.manage">
                <StudentBadgeTypes />
              </ProtectedRoute>
            } />
            {/* Okul katalogu: ogretmen/stajyer de gorebilmeli (ogrenci
                formundaki secim kutusu bu listeyi kullaniyor). Ekleme ve
                duzenleme sayfa icinde schools.manage iznine gore gizleniyor,
                asil kisit backend'deki route middleware'i. */}
            <Route path="schools" element={
              <StaffRoute>
                <Schools />
              </StaffRoute>
            } />

            <Route path="users" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="users.edit">
                <Users />
              </ProtectedRoute>
            } />
            <Route path="users/:id" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="users.edit">
                <UserDetail />
              </ProtectedRoute>
            } />
            <Route path="leave-requests" element={
              <ProtectedRoute roles={MANAGER_ROLES} permission="leaves.view">
                <LeaveRequests />
              </ProtectedRoute>
            } />
            <Route path="delegations" element={
              <ProtectedRoute roles={MANAGER_ROLES}>
                <Delegations />
              </ProtectedRoute>
            } />
            <Route path="settings" element={
              <ProtectedRoute roles={ADMIN_ROLES}>
                <Settings />
              </ProtectedRoute>
            } />
          </Route>
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;